/**
 * Groups processing steps into a parent/child hierarchy.
 *
 * Agent Progress messages become parent nodes. Tool messages that follow
 * become children nested under the preceding agent message. System messages
 * (Safety, Tracing, Metrics, etc.) stay as flat top-level items.
 */
import { ProcessingStepMessage } from "@/components/chat/types";
import { ProcessingStepSection } from "@/types/chat-response";

export interface GroupedStep {
  /** The parent step (agent thinking, or a standalone system step). */
  step: ProcessingStepMessage;
  /** Child steps nested under this parent (tool calls, errors, etc.). */
  children: ProcessingStepMessage[];
}

/** Sections that act as "parent" nodes — agent thinking starts a new group. */
const isAgentSection = (section: string): boolean =>
  section === ProcessingStepSection.AGENT_PROGRESS ||
  section === ProcessingStepSection.AGENT_PROCESSING ||
  section === ProcessingStepSection.PROCESSING;

/** Sections that are "child" nodes — tools nest under the preceding agent step. */
const isToolSection = (section: string): boolean =>
  section === ProcessingStepSection.TOOL_PROGRESS ||
  section === ProcessingStepSection.TOOL_EXECUTION_DETAILS ||
  section.startsWith("Tool:");

/**
 * Group a flat list of processing steps into a parent/child hierarchy.
 *
 * Rules:
 * - An Agent Progress message starts a new group (becomes the parent).
 * - Tool messages that follow are nested as children of the current group.
 * - System messages (Safety, Tracing, Final Response, Metrics) are standalone
 *   top-level items with no children.
 * - If tool messages appear before any agent message, they get their own
 *   standalone group.
 */
export const groupSteps = (steps: ProcessingStepMessage[]): GroupedStep[] => {
  const groups: GroupedStep[] = [];
  let currentGroup: GroupedStep | null = null;

  for (const step of steps) {
    const section = step.section as string;

    if (isAgentSection(section)) {
      // Agent message → start a new group
      currentGroup = { step, children: [] };
      groups.push(currentGroup);
    } else if (isToolSection(section)) {
      // Tool message → nest under current agent group
      if (currentGroup) {
        currentGroup.children.push(step);
      } else {
        // No agent parent yet — make it standalone
        groups.push({ step, children: [] });
      }
    } else {
      // System message (Safety, Tracing, Final Response, Metrics) → standalone
      groups.push({ step, children: [] });
      // Don't reset currentGroup — tools after a system message
      // should still attach to the last agent group if one exists
    }
  }

  return groups;
};
